import axios from "axios";
import { useDrupalSettings } from "../composables/useDrupalSettings.js";

const { config } = useDrupalSettings();

const jsonapiClient = {
  headers: { "Content-Type": "application/vnd.api+json" },
  call: {},
  initAxiosClient() {
    axios.defaults.baseURL = config.baseUrl + "/jsonapi";
    axios.defaults.headers.common.Authorization = "Bearer " + config.jwt;
    axios.defaults.headers.common["X-CSRF-Token"] = config.csrfToken;
    axios.defaults.headers.common.crossDomain = true;
    axios.interceptors.response.use(null, (error) => {
      if (axios.isCancel(error)) {
        console.log("Request canceled", error.message);
      } else {
        jsonapiClient.logErrors(error);
        return Promise.reject(error);
      }
    });
  },
  getErrorResponseData(error) {
    let data = null;
    if (
      typeof error.response !== "undefined" &&
      typeof error.response.headers !== "undefined" &&
      typeof error.response.headers["content-type"] !== "undefined" &&
      error.response.headers["content-type"] === "application/vnd.api+json"
    ) {
      data = error.response.data ? error.response.data : error.response;
    }
    return data;
  },
  getJSONapi(path, queries, callback, error_callback, context) {
    // @todo fix this so only a queries object can be passed in.
    let querystring = "";

    if (typeof queries === "string") {
      querystring = "?" + queries;
    }
    if (typeof queries !== "object" || !queries) {
      queries = {};
    }

    this.initAxiosClient();
    return axios
      .get(path + querystring, {
        params: queries,
      })
      .then((response) => {
        callback.call(context, response.data);
      })
      .catch((error) => {
        if (typeof error_callback === "function") {
          error_callback.call(
            context,
            error.message,
            jsonapiClient.getErrorResponseData(error),
          );
        } else {
          console.error(error);
        }
      });
  },
  logErrors(error) {
    // Only log for specified errors.
    const logfor = [403, 404, 422, 500];
    if (
      typeof error.response !== "undefined" &&
      typeof error.response.status !== "undefined" &&
      logfor.indexOf(error.response.status) === -1
    ) {
      return;
    }
    // Prevent duplicates/recursion
    if (typeof error.logSent !== "undefined" && error.logSent) {
      return;
    }
    error.logSent = true;

    // get response data if we can
    let response = "[non-JSON response]";
    if (
      typeof error.response !== "undefined" &&
      typeof error.response.headers !== "undefined" &&
      typeof error.response.headers["content-type"] !== "undefined" &&
      error.response.headers["content-type"] === "application/vnd.api+json"
    ) {
      response = error.response.data;
    } else if (typeof error.response === "undefined") {
      error.response = { status: 0, statusText: "No response" };
    }

    const axiosClient = axios.create({
      baseURL: config.baseUrl,
    });

    const json_message = {
      message: error.message,
      url: error.config.url,
      method: error.config.method,
      data: error.config.data,
      response: response,
      status: error.response.status,
      statusText: error.response.statusText,
      module: config.module,
    };
    axiosClient
      .post(
        config.baseUrl + "/raft/rest/log?_format=json",
        {
          message: JSON.stringify(json_message),
          channel: json_message.module,
        },
        {
          headers: { "Content-Type": "application/json" },
        },
      )
      .catch((error) => {
        console.error(error);
      });
    this.headers["Content-Type"] = "application/vnd.api+json";
  },
  patchJSONapi(path, data, callback, error_callback, context) {
    this.initAxiosClient();
    return axios
      .patch(
        path,
        {
          data: data,
        },
        {
          headers: this.headers,
        },
      )
      .then((response) => {
        callback.call(context, response.data);
      })
      .catch((error) => {
        if (typeof error_callback === "function") {
          error_callback.call(
            context,
            error.message,
            jsonapiClient.getErrorResponseData(error),
          );
        } else {
          console.error(error);
        }
      });
  },
  postJSONapi(path, data, callback, error_callback, context) {
    this.initAxiosClient();
    return axios
      .post(
        path,
        {
          data: data,
        },
        {
          headers: this.headers,
        },
      )
      .then((response) => {
        callback.call(context, response.data);
      })
      .catch((error) => {
        if (typeof error_callback === "function") {
          error_callback.call(
            context,
            error.message,
            jsonapiClient.getErrorResponseData(error),
          );
        } else {
          console.error(error);
        }
      });
  },
  postJSONapiFile(path, file, callback, error_callback, context) {
    const headers = {};
    Object.assign(headers, this.headers);
    headers["Content-Disposition"] = 'file; filename="' + file.name + '"';
    headers["Content-Type"] = "application/octet-stream";
    this.initAxiosClient();
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onload = function () {
      axios
        .post(path, reader.result, { headers: headers })
        .then((response) => {
          callback.call(context, response.data);
        })
        .catch((error) => {
          if (typeof error_callback === "function") {
            error_callback.call(
              context,
              error.message,
              jsonapiClient.getErrorResponseData(error),
            );
          } else {
            console.error(error);
          }
        });
    };
  },
  getAsPostJSONapi(path, data, callback, context, autoCancel) {
    this.initAxiosClient();
    if (autoCancel && this.call[path] && typeof this.call[path] === "object") {
      this.call[path].cancel("Canceled overlapping call to " + path);
    }
    this.call[path] = axios.CancelToken.source();
    return axios
      .post(path, data, {
        cancelToken: this.call[path].token,
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-HTTP-Method-Override": "GET",
        },
      })
      .then((response) => {
        if (response) {
          callback.call(context, response.data);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  },
};

export default jsonapiClient;
